import { FC } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { FF, SearchInputDebounced, Validators } from 'ui/components'
import { DatePicker } from 'ui/components/DatePicker'
import { Select } from 'ui/components/Select'
import { FilterPanel } from 'ui/components/Table'
import { FilterWrapper } from 'ui/temp/Filter'
import { TableFilterRow } from 'ui/temp/TableFilterRow'
import { HistoryAuditListPayload } from '~bank-entity-history/types'

import { FilterProps } from './Filter.types'

import * as SC from './styles'

export const Filter: FC<FilterProps> = (props) => {
  const { data, setFilter } = props
  const { config, actionOptions, bankUserOptions, entityTypeOptions } = data || {}

  const formApi = useForm({
    defaultValues: config as Omit<HistoryAuditListPayload, 'size' | 'lastID'>
  })
  const isDirty = useStore(formApi.store, (s) => s.isDirty)

  const clear = () => {
    formApi.reset()
    setFilter(undefined)
  }

  const onFilterChange = (field: string, value: string | undefined) => {
    setFilter((prev) => ({ ...prev, [field]: value, lastID: undefined }) as HistoryAuditListPayload)
  }

  return (
    <FilterPanel isDirty={isDirty} onReset={clear} expanded>
      <FilterWrapper>
        <TableFilterRow>
          <SC.FixedColumn>
            <formApi.Field
              name="timeTo"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  if (value) {
                    onFilterChange(fieldApi.name, value)
                  }

                  return Validators.required.field(value)
                }
              }}
              children={(field) => (
                <FF field={field} label="Date To" input={(p) => <DatePicker {...p} showTimeSelect />} />
              )}
            />
          </SC.FixedColumn>
          <SC.FixedColumn>
            <formApi.Field
              name="timeFrom"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  onFilterChange(fieldApi.name, value)
                  return undefined
                }
              }}
              children={(field) => (
                <FF field={field} label="Date From" input={(p) => <DatePicker {...p} showTimeSelect />} />
              )}
            />
          </SC.FixedColumn>
          <SC.FixedColumn>
            <formApi.Field
              name="entityType"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  onFilterChange(fieldApi.name, value)
                  return undefined
                }
              }}
              children={(field) => (
                <FF
                  field={field}
                  label="Entity Type"
                  input={(p) => (
                    <Select {...p} data={entityTypeOptions || []} options-data-qa="Entity" data-qa="Entity Type" />
                  )}
                />
              )}
            />
          </SC.FixedColumn>
          <SC.FixedColumn>
            <formApi.Field
              name="entityId"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  onFilterChange(fieldApi.name, value)
                  return undefined
                }
              }}
              children={(field) => (
                <FF field={field} label="Entity ID" input={(p) => <SearchInputDebounced {...p} />} />
              )}
            />
          </SC.FixedColumn>
          <SC.FixedColumn>
            <formApi.Field
              name="action"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  onFilterChange(fieldApi.name, value)
                  return undefined
                }
              }}
              children={(field) => (
                <FF field={field} label="Action Type" input={(p) => <Select {...p} data={actionOptions || []} />} />
              )}
            />
          </SC.FixedColumn>
          <SC.FixedColumn>
            <formApi.Field
              name="bankUserId"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  onFilterChange(fieldApi.name, value)
                  return undefined
                }
              }}
              children={(field) => (
                <FF
                  field={field}
                  label="Bank User"
                  input={(p) => <Select {...p} data={bankUserOptions || []} searchable />}
                />
              )}
            />
          </SC.FixedColumn>
        </TableFilterRow>
      </FilterWrapper>
    </FilterPanel>
  )
}
